import React, { useEffect, useMemo, useRef, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { useDispatch, useSelector } from "react-redux";
import { CircularProgress } from "@mui/material";
import details from "./details.module.css";
import courseVideo from "./videos.module.css";
import VideoJS from "../../component/VideoJs";
import FooterComponent from "../../component/FooterComponent";
import MasterclassCompletionModal from "../../component/MasterclassCompletionModal";
import PaymentPopup from "../../component/PaymentPopup";
import LoginPopup from "../../component/LoginPopup";
import { getCourseDetails } from "../redux/courseSlice";
import { getProfile } from "../redux/userSlice";
import Toastify from "../helper/Toastify";
import api from "../helper/Api";
import rocketImage from "../../image/rocket.png";
import enrollCourseImage from "../../image/enrollCourse.jpg";
import masterclassThumbnail from "../../image/masterclassImg.png";

const MasterclassVideo = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const { courseDetailsData, isLoading } = useSelector(
    (state) => state.courses
  );
  const userProfile = useSelector((state) => state.user);
  const playerRef = useRef(null);
  const completionShownRef = useRef(false);

  const [showCompletionModal, setShowCompletionModal] = useState(false);
  const [showPaymentPopup, setShowPaymentPopup] = useState(false);
  const [showLoginPopup, setShowLoginPopup] = useState(false);
  const [pendingPaymentAction, setPendingPaymentAction] = useState(false);

  const course = courseDetailsData?.course;
  const trailerUrl = course?.trailer?.[0]?.url;
  const isAuthenticated = !!localStorage.getItem("token");
  const purchasedCourseId = userProfile?.user?.courses?.find(
    (item) => item === id
  );
  const isEnrolled = Boolean(purchasedCourseId);

  const displayPrice = course?.price || 0;
  const formatIndianPrice = (value) =>
    Math.round(Number(value) || 0).toLocaleString("en-IN");

  useEffect(() => {
    dispatch(getCourseDetails(id));
    if (localStorage.getItem("token")) {
      dispatch(getProfile());
    }
    window.scrollTo(0, window.innerHeight * 0.1);
  }, [dispatch, id]);

  useEffect(() => {
    if (!course) return;
    if (!trailerUrl) {
      Toastify.warning("Masterclass video is not available");
      navigate(`/courses/Courses/CourseDetails/${id}`, { replace: true });
    }
  }, [course, trailerUrl, id, navigate]);

  const formatDate = (date) => {
    if (!date) return "";
    return new Date(date).toLocaleDateString("en-US", {
      day: "numeric",
      month: "short",
      year: "numeric",
    });
  };

  const videoJsOptions = useMemo(
    () => ({
      controls: true,
      responsive: true,
      fluid: true,
      autoplay: false,
      muted: true,
      poster: masterclassThumbnail,
      sources: trailerUrl ? [{ src: trailerUrl, type: "video/mp4" }] : [],
      playbackRateMenuButton: true,
      aspectRatio: "16:9",
    }),
    [trailerUrl]
  );

  const saveWatchProgress = async (videoCompleted = false, playedSeconds = null) => {
    if (!isAuthenticated || !id) return;

    const player = playerRef.current;
    const watchedDuration =
      playedSeconds ?? player?.currentTime?.() ?? 0;
    const totalDuration = player?.duration?.() || 0;
    if (!totalDuration) return;

    try {
      await api.updateMasterclassWatchProgress({
        courseId: id,
        watchedDuration,
        totalDuration,
        videoCompleted,
      });
    } catch (error) {
      console.error("Failed to update masterclass watch progress", error);
    }
  };

  const handlePlayerReady = (player) => {
    playerRef.current = player;

    player.on("ended", () => {
      saveWatchProgress(true);
      if (completionShownRef.current) return;
      completionShownRef.current = true;
      setShowCompletionModal(true);
    });
  };

  const handleEnrollNow = () => {
    setShowCompletionModal(false);
    if (!isAuthenticated) {
      setPendingPaymentAction(true);
      setShowLoginPopup(true);
    } else {
      setShowPaymentPopup(true);
    }
  };

  const handleGoToCourse = () => {
    setShowCompletionModal(false);
    navigate(`/courses/Courses/CourseDetails/CoursesVideos/${id}`);
  };

  if (isLoading && !course) {
    return (
      <div className="flex min-h-[60vh] items-center justify-center">
        <CircularProgress />
      </div>
    );
  }

  return (
    <div>
      {showLoginPopup && (
        <LoginPopup
          isOpen={showLoginPopup}
          onClose={() => {
            setShowLoginPopup(false);
            setPendingPaymentAction(false);
          }}
          onSuccess={() => {
            dispatch(getProfile());
            if (pendingPaymentAction) {
              setShowPaymentPopup(true);
              setPendingPaymentAction(false);
            }
          }}
        />
      )}

      {showPaymentPopup && (
        <PaymentPopup
          courseDetails={course}
          onClose={() => {
            setShowPaymentPopup(false);
            setPendingPaymentAction(false);
          }}
          onProceedToPayment={() => {
            if (!isAuthenticated) {
              setShowPaymentPopup(false);
              setShowLoginPopup(true);
              setPendingPaymentAction(true);
            }
          }}
          isAuthenticated={isAuthenticated}
        />
      )}

      <MasterclassCompletionModal
        isOpen={showCompletionModal}
        onClose={() => setShowCompletionModal(false)}
        course={course}
        isEnrolled={isEnrolled}
        onEnrollNow={handleEnrollNow}
        onGoToCourse={handleGoToCourse}
      />

      <div className={details.mainContainer}>
        <div className={details.childContent}>
          <div
            className={courseVideo.courseVideo}
            onClick={() => navigate(`/courses/Courses/CourseDetails/${id}`)}
          >
            Back to course
          </div>
        </div>

        <div
          className={courseVideo.childContent}
          style={{ marginTop: 20, justifyContent: "center" }}
        >
          <div className={courseVideo.videoPlayer} style={{ width: "90%" }}>
            <div className={courseVideo.videoContainer}>
              <div className={courseVideo.videoPlayerWrapper}>
                {trailerUrl ? (
                  <div>
                    <VideoJS
                      options={videoJsOptions}
                      onReady={handlePlayerReady}
                      updateVideoDuration={(playedSeconds) => {
                        const totalDuration = playerRef.current?.duration?.() || 0;
                        if (!totalDuration) return;
                        saveWatchProgress(
                          playedSeconds >= totalDuration - 1,
                          playedSeconds
                        );
                      }}
                      urlData={trailerUrl}
                      showRecordedText={false}
                    />
                  </div>
                ) : (
                  <div className="flex min-h-[320px] items-center justify-center bg-black">
                    <CircularProgress color="inherit" />
                  </div>
                )}
              </div>
            </div>

            <div className={courseVideo.courseCard}>
              <div
                style={{
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "space-between",
                }}
              >
                <p className={courseVideo.lastUpdated}>
                  Last Updated: <span>{formatDate(course?.updatedAt)}</span>
                </p>
                <div
                  style={{
                    color: "rgba(29, 131, 248, 1)",
                    background: "rgba(29, 131, 248, 0.1)",
                    padding: "5px 15px",
                    fontWeight: "600",
                    fontSize: "12px",
                    borderRadius: "16px",
                  }}
                >
                  • Free Masterclass
                </div>
              </div>

              <h2 className={courseVideo.courseTitle}>{course?.title}</h2>
              <p className={courseVideo.courseDescription}>
                {course?.subtitle}
              </p>
            </div>
          </div>
        </div>

        {/* Enroll Course - With Gaps and Rounded Corners */}
        <div className="px-4 sm:px-6 md:px-8 lg:px-12 xl:px-16 mb-16">
          <div
            className="relative overflow-hidden w-full rounded-2xl sm:rounded-3xl"
            style={{
              backgroundImage: `url(${course?.courseEntrollBanner?.[0] || enrollCourseImage
                })`,
              backgroundSize: "cover",
              backgroundPosition: "center",
              backgroundRepeat: "no-repeat",
            }}
          >
            <div className="relative z-10 px-4 sm:px-6 md:px-8 lg:px-12 xl:px-16 py-8 sm:py-10 md:py-12 lg:py-14 text-white">
              {/* Content remains the same */}
              <p className="max-w-[1400px] text-base sm:text-[18px] md:text-[20px] lg:text-[22px] leading-[165%] text-white/95">
                {course?.courseEntrollDescription}
              </p>

              <div className="relative mt-6">
                <img
                  src={rocketImage}
                  alt=""
                  className="absolute right-0 top-[-50px] sm:top-[-60px] md:top-[-70px] w-[150px] sm:w-[200px] md:w-[240px] lg:w-[390px] object-contain z-20 pointer-events-none"
                />

                <div className="inline-flex flex-col sm:flex-row border border-white/40 rounded-[20px] overflow-hidden bg-[#000D36]/25 backdrop-blur-sm relative z-10 w-full sm:w-auto">
                  <div className="px-4 sm:px-6 md:px-8 py-5 sm:py-6 md:py-7 w-full sm:w-auto">
                    <h3 className="text-xl sm:text-[22px] md:text-[24px] font-semibold text-white/90">
                      Program Value
                    </h3>
                    <div className="mt-3 sm:mt-4 flex flex-wrap items-center gap-2 sm:gap-3">
                      <span className="text-[#FF5A00] text-3xl sm:text-[40px] md:text-[48px] font-bold line-through">
                        ₹1,00,000
                      </span>
                      <span className="bg-[#1557FF] px-2 sm:px-3 py-1 rounded text-xs sm:text-sm font-semibold">
                        +GST
                      </span>
                    </div>
                  </div>

                  <div className="w-full sm:w-[3px] h-[3px] sm:h-auto bg-[#FF5A00]" />

                  <div className="px-4 sm:px-6 md:px-8 py-5 sm:py-6 md:py-7 w-full sm:w-auto">
                    <h3 className="text-xl sm:text-[22px] md:text-[24px] font-semibold text-white/90">
                      Today Only
                    </h3>
                    <div className="mt-3 sm:mt-4 flex flex-wrap items-center gap-2 sm:gap-3">
                      <span className="text-[#FF5A00] text-3xl sm:text-[40px] md:text-[48px] font-bold">
                        ₹{formatIndianPrice(displayPrice)}/-
                      </span>
                      <span className="bg-[#1557FF] px-2 sm:px-3 py-1 rounded text-xs sm:text-sm font-semibold">
                        +GST
                      </span>
                    </div>
                  </div>
                </div>
              </div>

              <div className="flex flex-col lg:flex-row lg:items-center gap-6 sm:gap-8 lg:gap-10 mt-6 sm:mt-8 lg:mt-4">
                <div className="w-full sm:w-auto">
                  {purchasedCourseId ? (
                    <div className="relative inline-flex p-3 border-2 border-dashed border-white/70 rounded-md w-full sm:w-auto">
                      <span className="absolute -top-2 -left-2 h-3.5 w-3.5 rounded-full bg-white" />
                      <span className="absolute -top-2 -right-2 h-3.5 w-3.5 rounded-full bg-white" />
                      <span className="absolute -bottom-2 -left-2 h-3.5 w-3.5 rounded-full bg-white" />
                      <span className="absolute -bottom-2 -right-2 h-3.5 w-3.5 rounded-full bg-white" />
                      <button
                        type="button"
                        onClick={handleGoToCourse}
                        className="w-full sm:w-auto h-[56px] sm:h-[66px] md:h-[76px] px-6 sm:px-8 md:px-12 bg-[#FC3602] hover:bg-[#e43100] text-white text-xl sm:text-2xl md:text-[28px] font-bold uppercase rounded-[8px] transition"
                      >
                        GO TO COURSE
                      </button>
                      <span className="absolute -right-6 -bottom-7 sm:-right-7 sm:-bottom-8">
                        <svg
                          width="28"
                          height="28"
                          viewBox="0 0 24 24"
                          fill="none"
                          xmlns="http://www.w3.org/2000/svg"
                          aria-hidden
                        >
                          <path
                            d="M3 2L21 10L13 13L10 21L3 2Z"
                            fill="#FC3602"
                            stroke="#FC3602"
                            strokeWidth="1.5"
                            strokeLinejoin="round"
                          />
                        </svg>
                      </span>
                    </div>
                  ) : (
                    <div className="relative inline-flex p-3 border-2 border-dashed border-white/70 rounded-md w-full sm:w-auto">
                      <span className="absolute -top-2 -left-2 h-3.5 w-3.5 rounded-full bg-white" />
                      <span className="absolute -top-2 -right-2 h-3.5 w-3.5 rounded-full bg-white" />
                      <span className="absolute -bottom-2 -left-2 h-3.5 w-3.5 rounded-full bg-white" />
                      <span className="absolute -bottom-2 -right-2 h-3.5 w-3.5 rounded-full bg-white" />
                      <button
                        type="button"
                        onClick={() => {
                          setShowPaymentPopup(true);
                          if (!isAuthenticated) {
                            setPendingPaymentAction(true);
                          }
                        }}
                        className="w-full sm:w-auto h-[56px] sm:h-[66px] md:h-[76px] px-6 sm:px-8 md:px-12 bg-[#FC3602] hover:bg-[#e43100] text-white text-xl sm:text-2xl md:text-[28px] font-bold uppercase rounded-[8px] transition"
                      >
                        ENROLL NOW
                      </button>
                      <span className="absolute -right-6 -bottom-7 sm:-right-7 sm:-bottom-8">
                        <svg
                          width="28"
                          height="28"
                          viewBox="0 0 24 24"
                          fill="none"
                          xmlns="http://www.w3.org/2000/svg"
                          aria-hidden
                        >
                          <path
                            d="M3 2L21 10L13 13L10 21L3 2Z"
                            fill="#FC3602"
                            stroke="#FC3602"
                            strokeWidth="1.5"
                            strokeLinejoin="round"
                          />
                        </svg>
                      </span>
                    </div>
                  )}
                </div>
              </div>

              {course?.bestSuitFor?.length > 0 && (
                <div className="mt-8 sm:mt-10 text-center sm:text-left">
                  <p className="text-white/80 text-sm sm:text-base mb-3 sm:mb-4">
                    Best suited for ➜
                  </p>
                  <div className="flex flex-wrap gap-3 sm:gap-4 justify-center sm:justify-start">
                    {course.bestSuitFor.map((item, index) => (
                      <span
                        key={index}
                        className="px-4 sm:px-6 md:px-7 h-10 sm:h-11 md:h-12 rounded-full bg-black border-2 border-[grey] flex items-center justify-center text-white text-xs sm:text-sm font-medium whitespace-nowrap"
                      >
                        {item}
                      </span>
                    ))}
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>
        <div className={courseVideo.footerStyle}>
          <FooterComponent />
        </div>
      </div>
    </div>
  );
};

export default MasterclassVideo;
